<script setup>
// NOTICE: KAPTION-INTEGRATION
// team-assignment-resolution.UI.1 — account settings section for the "always a
// person" team-assignment mode; persists settings.team_assignment_resolution
// through accounts#update. Self-gated by KAPTION_TEAM_ASSIGNMENT_RESOLUTION
// (team-assignment-resolution.UI.2 — availability switch, default OFF, like
// SecureAssignmentMode). Reflects the persisted value and falls back to 'team'
// when unset (team-assignment-resolution.UI.3).
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import SectionLayout from './SectionLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const selectedMode = ref('team');
const isSubmitting = ref(false);

const { currentAccount, updateAccount } = useAccount();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

const isFlagEnabled = computed(() =>
  isKaptionFlagEnabled('KAPTION_TEAM_ASSIGNMENT_RESOLUTION')
);

const options = [
  {
    value: 'team',
    labelKey: 'GENERAL_SETTINGS.FORM.TEAM_ASSIGNMENT_RESOLUTION.OPTION_TEAM',
    descKey:
      'GENERAL_SETTINGS.FORM.TEAM_ASSIGNMENT_RESOLUTION.OPTION_TEAM_DESC',
  },
  {
    value: 'always_person',
    labelKey:
      'GENERAL_SETTINGS.FORM.TEAM_ASSIGNMENT_RESOLUTION.OPTION_ALWAYS_PERSON',
    descKey:
      'GENERAL_SETTINGS.FORM.TEAM_ASSIGNMENT_RESOLUTION.OPTION_ALWAYS_PERSON_DESC',
  },
  {
    value: 'only_person',
    labelKey:
      'GENERAL_SETTINGS.FORM.TEAM_ASSIGNMENT_RESOLUTION.OPTION_ONLY_PERSON',
    descKey:
      'GENERAL_SETTINGS.FORM.TEAM_ASSIGNMENT_RESOLUTION.OPTION_ONLY_PERSON_DESC',
  },
];

watch(
  currentAccount,
  () => {
    const mode =
      currentAccount.value?.settings?.team_assignment_resolution || 'team';
    selectedMode.value = mode;
  },
  { deep: true, immediate: true }
);

const handleSubmit = async () => {
  try {
    isSubmitting.value = true;
    await updateAccount(
      { team_assignment_resolution: selectedMode.value },
      { silent: true }
    );
    useAlert(t('GENERAL_SETTINGS.FORM.TEAM_ASSIGNMENT_RESOLUTION.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.TEAM_ASSIGNMENT_RESOLUTION.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <SectionLayout
    v-if="isFlagEnabled"
    :title="t('GENERAL_SETTINGS.FORM.TEAM_ASSIGNMENT_RESOLUTION.TITLE')"
    :description="
      t('GENERAL_SETTINGS.FORM.TEAM_ASSIGNMENT_RESOLUTION.DESCRIPTION')
    "
    with-border
  >
    <form class="grid gap-4" @submit.prevent="handleSubmit">
      <div
        class="rounded-xl border border-n-weak bg-n-solid-1 w-full text-sm text-n-slate-12 divide-y divide-n-weak"
      >
        <label
          v-for="option in options"
          :key="option.value"
          class="p-3 flex items-start gap-3 cursor-pointer hover:bg-n-alpha-1 transition-colors"
          :class="{ 'bg-n-alpha-1': selectedMode === option.value }"
        >
          <input
            v-model="selectedMode"
            type="radio"
            name="team_assignment_resolution"
            :value="option.value"
            class="mt-0.5 accent-woot-500"
          />
          <div>
            <span class="font-medium">{{ t(option.labelKey) }}</span>
            <p class="text-n-slate-11 text-xs mt-0.5">
              {{ t(option.descKey) }}
            </p>
          </div>
        </label>
      </div>
      <div class="flex gap-2">
        <NextButton
          blue
          type="submit"
          :is-loading="isSubmitting"
          :label="
            t('GENERAL_SETTINGS.FORM.TEAM_ASSIGNMENT_RESOLUTION.UPDATE_BUTTON')
          "
        />
      </div>
    </form>
  </SectionLayout>
</template>
